// =============================================================================
// FILE: packages/renderer/src/components/branding/MiniEcgMonitor.tsx
// PURPOSE: RCD² "vitals" heartbeat monitor. A pulse of light travels along an
//          ECG waveform. The pace auto-scales with workload (fewer active
//          projects → calmer/slower beat; more projects → faster), and a small
//          option button lets the operator force Slower / Auto / Faster. The
//          chosen mode is shared across every monitor instance (sidebar +
//          landing) and persisted to localStorage.
// =============================================================================

import { useEffect, useState, type JSX } from 'react';
import { Heart, Gauge } from 'lucide-react';
import { playBeep } from '../../lib/soundManager.js';
import { useConnectivityMode } from '../../hooks/useConnectivityMode.js';

// Two-beat ECG waveform, normalised via pathLength=100 so the light pulse can
// be expressed as a percentage dash that travels the entire trace.
const ECG_PATH =
  'M0,17 L18,17 L22,17 L26,7 L30,27 L34,10 L38,17 L58,17 L62,17 L66,7 L70,27 L74,10 L78,17 L98,17 L102,17 L106,7 L110,27 L114,10 L118,17 L120,17';

// ── Shared pace mode (synced across all monitors) ────────────────────────────
type PaceMode = 'auto' | 'slow' | 'fast';
const MODE_KEY = 'rcd2.ecg.mode';
const MODE_EVENT = 'rcd2:ecg-mode';
const MODES: PaceMode[] = ['auto', 'slow', 'fast'];

function readMode(): PaceMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (v === 'slow' || v === 'fast' || v === 'auto') return v;
  } catch { /* */ }
  return 'auto';
}

function cycleMode(): void {
  const next = MODES[(MODES.indexOf(readMode()) + 1) % MODES.length]!;
  try { localStorage.setItem(MODE_KEY, next); } catch { /* */ }
  window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: next }));
}

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));

interface Props {
  width?: number;
  height?: number;
  showReadout?: boolean;
  /** Active project count — drives the auto pace (more projects → faster). */
  projectCount?: number;
  /** Warp Drive engaged — heartbeat races. */
  energized?: boolean;
  /** When provided, clicking the monitor runs this instead of freezing (e.g. open R2 Brain / Capabilities). */
  onOpen?: () => void;
}

export function MiniEcgMonitor({ width = 78, height = 34, showReadout = true, projectCount, energized = false, onOpen }: Props): JSX.Element {
  const [mode, setMode] = useState<PaceMode>(readMode);

  // Sync mode across instances + tabs.
  useEffect(() => {
    const onMode = (e: Event) => setMode((e as CustomEvent<PaceMode>).detail);
    const onStorage = (e: StorageEvent) => { if (e.key === MODE_KEY) setMode(readMode()); };
    window.addEventListener(MODE_EVENT, onMode);
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener(MODE_EVENT, onMode); window.removeEventListener('storage', onStorage); };
  }, []);

  // Workload → BPM. Calm baseline when idle; climbs with active projects.
  const load = projectCount ?? 0;
  const manualAdj = mode === 'slow' ? -12 : mode === 'fast' ? 20 : 0;
  const warpAdj = energized ? 55 : 0;   // Warp Drive engaged — heartbeat races
  const bpm = clamp(Math.round(50 + load * 3 + manualAdj + warpAdj), 42, 190);
  // Visual travel time (seconds) — inversely proportional to BPM.
  const dur = clamp(360 / bpm, 1.5, 8).toFixed(2);

  const modeLabel = mode === 'slow' ? 'SLOW' : mode === 'fast' ? 'FAST' : 'AUTO';
  const [frozen, setFrozen] = useState(false);

  // Connectivity → vitals health. Green = healthy, Yellow = losing internet
  // (browser online but server unreachable), Red = offline & flatlined.
  const { serverReach } = useConnectivityMode();
  const online = typeof navigator === 'undefined' ? true : navigator.onLine;
  const health: 'green' | 'yellow' | 'red' = !online ? 'red' : serverReach === false ? 'yellow' : 'green';

  const THEME = {
    green:  { base: 'rgba(45,212,191,0.25)', pulse: '#7dffe8', head: '#ffffff', border: 'rgba(45,212,191,0.35)', glow: '45,212,191', pulseGlow: '125,255,232', grid: '45,212,191', read: 'rgba(45,212,191,0.85)' },
    yellow: { base: 'rgba(251,191,36,0.30)', pulse: '#fde68a', head: '#fffbeb', border: 'rgba(251,191,36,0.5)',  glow: '251,191,36', pulseGlow: '253,230,138', grid: '251,191,36', read: 'rgba(253,224,71,0.9)' },
    red:    { base: 'rgba(239,68,68,0.40)',  pulse: '#fca5a5', head: '#fecaca', border: 'rgba(239,68,68,0.55)',  glow: '239,68,68', pulseGlow: '252,165,165', grid: '239,68,68',  read: 'rgba(248,113,113,0.95)' },
  }[health];

  const connStopped = health === 'red';                    // no internet → flatline / stopped
  const playState = (frozen || connStopped) ? 'paused' : 'running';
  const statusLabel = health === 'red' ? 'OFFLINE' : health === 'yellow' ? 'WEAK SIGNAL' : modeLabel;

  const toggleFrozen = () => {
    if (connStopped) { playBeep(300); return; }             // can't resume while offline
    setFrozen((f) => {
      playBeep(f ? 660 : 990);  // lower beep to resume, higher to freeze
      return !f;
    });
  };

  return (
    <div data-testid="sidebar-ecg-monitor" className="flex flex-col items-end gap-1" title={`System Vitals · ${modeLabel} · ${bpm} BPM`}>
      <button
        type="button"
        data-testid="ecg-freeze-toggle"
        data-nosound
        onClick={(e) => { e.stopPropagation(); if (onOpen) { onOpen(); } else { toggleFrozen(); } }}
        title={onOpen ? 'Enter R2 Brain — the living intelligence centre' : connStopped ? 'No internet — vitals flatlined' : frozen ? 'Vitals frozen — tap to resume the heartbeat' : health === 'yellow' ? 'Weak signal — losing connection' : 'Tap to freeze the heartbeat'}
        style={{
          width, height,
          padding: 0,
          borderRadius: 6,
          background: '#04110f',
          border: `1px solid ${frozen && !connStopped ? 'rgba(252,211,77,0.5)' : THEME.border}`,
          boxShadow: `inset 0 0 12px ${THEME.glow ? `rgba(${THEME.glow},0.16)` : ''}, 0 0 10px rgba(${THEME.glow},0.18)`,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'block',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0,
            backgroundImage:
              `linear-gradient(rgba(${THEME.grid},0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(${THEME.grid},0.10) 1px, transparent 1px)`,
            backgroundSize: '8px 8px',
          }}
        />
        <svg width={width} height={height} viewBox="0 0 120 34" preserveAspectRatio="none" style={{ position: 'relative', display: 'block' }}>
          {connStopped ? (
            /* Flatline when offline */
            <path d="M0,17 L120,17" fill="none" stroke={THEME.pulse} strokeWidth="2" strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 4px rgba(${THEME.pulseGlow},0.8))` }} />
          ) : (
            <>
              <path d={ECG_PATH} fill="none" stroke={THEME.base} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              <path
                d={ECG_PATH}
                pathLength={100}
                fill="none"
                stroke={THEME.pulse}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  filter: `drop-shadow(0 0 3px rgba(${THEME.pulseGlow},0.95)) drop-shadow(0 0 6px rgba(${THEME.glow},0.7))`,
                  strokeDasharray: '14 86',
                  animation: `ecg-pulse-travel ${dur}s ease-in-out infinite`,
                  animationPlayState: playState,
                }}
              />
              <path
                d={ECG_PATH}
                pathLength={100}
                fill="none"
                stroke={THEME.head}
                strokeWidth="2.4"
                strokeLinecap="round"
                style={{
                  filter: `drop-shadow(0 0 4px rgba(${THEME.pulseGlow},0.95))`,
                  strokeDasharray: '2 98',
                  animation: `ecg-pulse-travel ${dur}s ease-in-out infinite`,
                  animationPlayState: playState,
                }}
              />
            </>
          )}
        </svg>
      </button>
      {showReadout && (
        <div className="flex items-center gap-1.5">
          <span style={{ fontSize: 7, letterSpacing: '0.10em', color: THEME.read, fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <Heart className={connStopped ? '' : 'animate-pulse'} style={{ width: 7, height: 7 }} fill="currentColor" /> {connStopped ? statusLabel : `${bpm} BPM`}
          </span>
          <button
            type="button"
            data-testid="ecg-speed-toggle"
            onClick={cycleMode}
            title={`Heartbeat pace: ${modeLabel} — click to change (Auto scales with active projects)`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 2,
              padding: '1px 4px', borderRadius: 4, cursor: 'pointer',
              background: 'rgba(45,212,191,0.10)', border: '1px solid rgba(45,212,191,0.3)',
              color: 'rgba(94,234,212,0.9)', fontSize: 6.5, fontFamily: 'monospace',
              letterSpacing: '0.12em', lineHeight: 1,
            }}
          >
            <Gauge style={{ width: 7, height: 7 }} strokeWidth={2.2} />
            {modeLabel}
          </button>
        </div>
      )}
    </div>
  );
}
